import { type Container, RendererType, type Renderer, type RenderTexture } from "pixi.js";

/** the parts of Pixi's Canvas 2D context system used here (it isn't in Pixi's public types) */
interface CanvasContextSystem {
    rootContext?: CanvasRenderingContext2D
    activeContext?: CanvasRenderingContext2D
    _activeBlendMode?: string
}

/**
 * Pixi's Canvas 2D renderer (what draws the game when the browser has no WebGL, as with graphics acceleration off)
 * keeps the canvas's composite operation from one frame to the next: a frame whose last picture was drawn with added
 * light (blendMode "add": a fire's glow, the plasma burst) left "lighter" on, and the next frame's first pictures (the
 * background, the ground, the loot and the players) were added onto each other: the whole world came out washed white
 * until something drawn normally came last again. Every frame now starts drawn normally (found in the lab, Pixi 8.19:
 * a page with nothing but a ground, a box and an added-light sprite drawn last showed the box as box + ground).
 */
export const resetCanvasBlendEachFrame = (renderer: Renderer): void => {
    if (renderer.type !== RendererType.CANVAS) return;
    const system = (renderer as unknown as { readonly canvasContext?: CanvasContextSystem }).canvasContext;
    if (!system) return;
    renderer.runners.prerender.add({
        prerender: () => {
            for (const context of [system.rootContext, system.activeContext]) {
                if (context) context.globalCompositeOperation = "source-over";
            }
            system._activeBlendMode = "normal";
        }
    });
};

/**
 * Draws `container` into `target` (a render texture), cleared first. With Pixi's Canvas 2D renderer a render texture is a
 * canvas of its own, which keeps the composite operation its last picture was drawn with, while Pixi takes the blend it
 * set last (anywhere) for the one in use: a texture whose last picture was an erased light ("destination-out": the
 * night's lights, managers/nightLight.ts, and the attic's, managers/atticDark.ts) drew its next frame's first picture,
 * the dark itself, as an eraser too (1.05, found in the lab). Its canvas starts each frame drawn normally
 */
export const renderInto = (renderer: Renderer, container: Container, target: RenderTexture): void => {
    if (renderer.type === RendererType.CANVAS) {
        const canvas = (target.source as { readonly resource?: unknown }).resource as { getContext?: (kind: "2d") => CanvasRenderingContext2D | null } | undefined;
        const context = typeof canvas?.getContext === "function" ? canvas.getContext("2d") : null;
        if (context) context.globalCompositeOperation = "source-over";
        const system = (renderer as unknown as { readonly canvasContext?: CanvasContextSystem }).canvasContext;
        if (system) system._activeBlendMode = "normal";
    }
    renderer.render({ container, target, clear: true });
};
